import React, { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import { Col, Card, Modal, ModalHeader, ModalBody } from "reactstrap";
import { useNavigate } from "react-router";
import DatePicker from 'react-datepicker';
import { baseURL } from '../../../Services/api/baseURL';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import { useDataContext } from '../../../context/hooks/useDataContext';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ── Pill tab button ────────────────────────────────────────────────────────────
const TabBtn = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '6px 18px',
      borderRadius: '999px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '13px',
      fontFamily: "'DM Sans', sans-serif",
      transition: 'all 0.18s ease',
      background: active ? '#D3178A' : 'transparent',
      color: active ? '#fff' : '#6b7280',
      boxShadow: active ? '0 2px 8px rgba(211,23,138,0.28)' : 'none',
    }}
  >
    {label}
  </button>
);

// ── Skeleton bar loader ────────────────────────────────────────────────────────
const SkeletonBars = () => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '220px', padding: '0 8px' }}>
    {Array.from({ length: 20 }).map((_, i) => (
      <div
        key={i}
        style={{
          flex: 1,
          borderRadius: '6px 6px 0 0',
          height: `${30 + Math.sin(i * 0.8) * 25 + Math.random() * 40}%`,
          background: 'linear-gradient(180deg, #f0f0f0 0%, #e0e0e0 100%)',
          animation: 'shimmer 1.4s ease-in-out infinite',
          animationDelay: `${i * 0.04}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes shimmer {
        0%,100% { opacity: 1 }
        50% { opacity: 0.45 }
      }
    `}</style>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
const BarChartClass = () => {
  const { categoryData } = useDataContext();
  const [activeTab, setActiveTab] = useState('order');
  const [chartData, setChartData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null); // { label, customers } for the customer list modal
  const navigate = useNavigate();

  // cancel previous in-flight request
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    const token = JSON.parse(localStorage.getItem('token'));
    const params = {};
    if (startDate) params.date = moment(startDate).format('YYYY-MM-DD');
    if (categoryId) params.categoryId = categoryId;

    try {
      const response = await axios.get(`${baseURL}/api/dashboard/get-chart-details`, {
        params,
        signal: abortRef.current.signal,
        headers: { Authorization: token },
      });

      if (response?.data?.success) {
        const d = response.data.data;
        const sorted = (arr) =>
          [...(arr || [])].sort(
            (a, b) =>
              new Date(a._id.year, a._id.month - 1, a._id.day) -
              new Date(b._id.year, b._id.month - 1, b._id.day)
          );

        if (activeTab === 'revenue') {
          setChartData(sorted(d?.revenue?.data));
          setTotalDataCount(d?.revenue?.totalCount?.[0]?.count ?? 0);
        } else if (activeTab === 'order') {
          setChartData(sorted(d?.order?.data));
          setTotalDataCount(d?.order?.totalCount?.[0]?.count ?? 0);
        } else {
          setChartData(sorted(d?.customer?.data));
          setTotalDataCount(d?.customer?.totalCount?.[0]?.count ?? 0);
        }
      }
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, startDate, categoryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Build chart dataset ──────────────────────────────────────────────────────
  const daysInMonth = moment(startDate).daysInMonth();
  const labels = Array.from({ length: daysInMonth }, (_, i) =>
    moment(startDate).startOf('month').add(i, 'days').format('DD')
  );

  const findEntry = (i) => {
    const date = moment(startDate).startOf('month').add(i, 'days').format('YYYY-MM-DD');
    return chartData.find(e => {
      const d = `${e._id.year}-${String(e._id.month).padStart(2, '0')}-${String(e._id.day).padStart(2, '0')}`;
      return d === date;
    });
  };

  // Customers who signed up on each day (customer tab only)
  const dayCustomers = labels.map((_, i) => findEntry(i)?.customers || []);

  const customerName = (c) =>
    [c?.first_name, c?.last_name].filter(Boolean).join(' ') || c?.email || c?.phoneNumber || 'Unnamed customer';

  const values = labels.map((_, i) => {
    const entry = findEntry(i);
    if (!entry) return 0;
    return activeTab === 'revenue'
      ? parseFloat(entry.totalCount?.toFixed(2) ?? 0)
      : parseFloat(entry.count?.toFixed(2) ?? 0);
  });

  const maxVal = Math.max(...values, 1);

  const barChartData = {
    labels,
    datasets: [
      {
        label: activeTab === 'revenue' ? 'Revenue ($)' : activeTab === 'order' ? 'Orders' : 'Customers',
        data: values,
        backgroundColor: values.map(v =>
          v === maxVal
            ? 'rgba(211,23,138,1)'
            : 'rgba(211,23,138,0.18)'
        ),
        hoverBackgroundColor: 'rgba(211,23,138,0.75)',
        borderRadius: 6,
        borderSkipped: false,
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400, easing: 'easeOutQuart' },
    onClick: (_evt, elements) => {
      if (activeTab !== 'customer' || !elements?.length) return;
      const i = elements[0].index;
      if (!dayCustomers[i]?.length) return;
      setSelectedDay({
        label: moment(startDate).startOf('month').add(i, 'days').format('DD MMM YYYY'),
        customers: dayCustomers[i],
      });
    },
    onHover: (evt, elements) => {
      const clickable = activeTab === 'customer' && elements?.length && dayCustomers[elements[0].index]?.length;
      if (evt?.native?.target) evt.native.target.style.cursor = clickable ? 'pointer' : 'default';
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1a1a2e',
        titleColor: '#fff',
        bodyColor: '#D3178A',
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) =>
            activeTab === 'revenue'
              ? `$${ctx.parsed.y.toFixed(2)}`
              : `${ctx.parsed.y}`,
          afterBody: (items) => {
            if (activeTab !== 'customer' || !items?.length) return [];
            const list = dayCustomers[items[0].dataIndex] || [];
            if (!list.length) return [];
            const lines = list.slice(0, 5).map(c => `• ${customerName(c)}`);
            if (list.length > 5) lines.push(`+${list.length - 5} more`);
            lines.push('Click bar to view all');
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 10, family: "'DM Sans', sans-serif" },
          color: '#9ca3af',
          maxRotation: 0,
          autoSkip: false,   // 👈 never skip a label
        },
        border: { display: false },
      },
      y: {
        grid: { color: '#f3f4f6', drawBorder: false },
        ticks: {
          font: { size: 11, family: "'DM Sans', sans-serif" },
          color: '#9ca3af',
          callback: (v) =>
            activeTab === 'revenue'
              ? `$${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`
              : v,
        },
        border: { display: false },
      },
    },
  };

  const capitalize = (s) => s?.charAt(0).toUpperCase() + s?.slice(1);

  const totalLabel =
    activeTab === 'revenue'
      ? `$${Number(totalDataCount).toFixed(2)}`
      : totalDataCount ?? 0;

  return (
    <Fragment>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <Col xl="12" md="12">
        <Card style={{ borderRadius: '16px', border: '1px solid #f0f0f0', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

          {/* ── Header ── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            padding: '18px 24px',
            borderBottom: '1px solid #f5f5f5',
          }}>
            {/* Left: title + total */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div>
                <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {moment(startDate).format('MMMM YYYY')}
                </p>
                <h5 style={{ margin: 0, fontSize: '22px', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>
                  {capitalize(activeTab)}
                  {activeTab !== 'customer' && (
                    <span style={{ marginLeft: '10px', fontSize: '15px', color: '#D3178A', fontWeight: 600 }}>
                      {totalLabel}
                    </span>
                  )}
                </h5>
              </div>
            </div>

            {/* Right: tabs + filters */}
            <div className='d-flex align-items-center file-content file-content-dash'  style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {/* Pill tabs */}
              <div style={{ display: 'flex', gap: '4px', background: '#f9fafb', borderRadius: '999px', padding: '4px' }}>
                {['revenue', 'order', 'customer'].map(tab => (
                  <TabBtn key={tab} label={capitalize(tab)} active={activeTab === tab} onClick={() => setActiveTab(tab)} />
                ))}
              </div>

              {/* Category select */}
              {/* <Input
                type="select"
                name="categoryId"
                onChange={(e) => setCategoryId(e.target.value)}
                style={{
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '6px 10px',
                  color: '#374151',
                  fontFamily: "'DM Sans', sans-serif",
                  height: '36px',
                  minWidth: '130px',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Category</option>
                {categoryData?.map(d => (
                  <option key={d._id} value={d._id}>{d.collection_name}</option>
                ))}
              </Input> */}

              {/* Month picker */}
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                showIcon
                placeholderText="Select Month"
                className="form-control"
                customInput={
                  <input style={{
                    fontSize: '13px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: '6px 12px',
                    color: '#374151',
                    fontFamily: "'DM Sans', sans-serif",
                    height: '36px',
                    width: '130px',
                    cursor: 'pointer',
                    outline: 'none',
                  }} />
                }
              />
            </div>
          </div>

          {/* ── Chart body ── */}
          <div style={{ padding: '20px 24px 16px', position: 'relative', minHeight: '280px' }}>
            {loading ? (
              <SkeletonBars />
            ) : (
              <div style={{ height: '400px' }}>
                <Bar data={barChartData} options={chartOptions} />
              </div>
            )}
          </div>

          {/* ── Footer summary strip ── */}
          {!loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '10px 24px 16px',
              gap: '6px',
            }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#D3178A', display: 'inline-block' }} />
              <span style={{ fontSize: '12px', color: '#6b7280', fontFamily: "'DM Sans', sans-serif" }}>
                Highest day highlighted
                {activeTab === 'customer' && ' · click a bar to see customers'}
              </span>
            </div>
          )}
        </Card>

        {/* ── Customers who signed up on the selected day ── */}
        <Modal isOpen={!!selectedDay} toggle={() => setSelectedDay(null)} size="lg" centered scrollable>
          <ModalHeader toggle={() => setSelectedDay(null)}>
            New customers · {selectedDay?.label} ({selectedDay?.customers?.length ?? 0})
          </ModalHeader>
          <ModalBody style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {selectedDay?.customers?.map((c, idx) => (
                  <tr
                    key={c._id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/customers/customer-detail/${c._id}`)}
                  >
                    <td>{idx + 1}</td>
                    <td style={{ color: '#D3178A', fontWeight: 600 }}>{customerName(c)}</td>
                    <td>{c.email || 'N/A'}</td>
                    <td>{c.phoneNumber || 'N/A'}</td>
                    <td>{moment(c.createdAt).format('hh:mm A')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ModalBody>
        </Modal>
      </Col>
    </Fragment>
  );
};

export default BarChartClass;






// import React, { Fragment, useEffect, useState } from 'react';
// import { Col, Card, CardBody, NavItem, NavLink, Nav, Input } from "reactstrap";
// import DatePicker from 'react-datepicker';
// import { Filter } from 'react-feather';
// import { baseURL } from '../../../Services/api/baseURL';
// import { Bar } from 'react-chartjs-2';
// import axios from 'axios';
// import { useDataContext } from '../../../context/hooks/useDataContext';
// import moment from 'moment';
// import jsPDF from 'jspdf';
// import 'jspdf-autotable';
// import * as XLSX from 'xlsx';

// const BarChartClass = () => {
//     const { categoryData } = useDataContext();
//     const [activeTab, setActiveTab] = useState('revenue');
//     const [data, setData] = useState([]);
//     const [storeData, setStoreData] = useState([]);
//     const [categoryId, setCategoryId] = useState("");
//     const [startDate, setStartDate] = useState(new Date());
//     const [totalDataCount, setTotalDataCount] = useState();
//     const [loading, setLoading] = useState(false);
//     const [revenueList, setRevenueList] = useState([]);

//     const handleChange = date => {
//         setStartDate(date);
//     };

//     const handleTabClick = (tab) => {
//         setActiveTab(tab);
//     };

//     const fetchStores = async () => {
//         const token = await JSON.parse(localStorage.getItem("token"));
//         try {
//             const stores = await axios.get(`${baseURL}/api/store/get-all-stores`, {
//                 headers: {
//                     Authorization: `${token}`,
//                 },
//             });
//             let filteredData = stores?.data?.data.filter((item) => item.status === "active");
//             setStoreData(filteredData || []);
//         } catch (error) {
//             console.log(error);
//         }
//     };

//     useEffect(() => {
//         const fetchData = async () => {
//             const token = await JSON.parse(localStorage.getItem("token"));
//             let params = {};
//             setLoading(true);
//             if (startDate) params.date = moment(startDate).format('YYYY-MM-DD');
//             if (categoryId) params.categoryId = categoryId;
//             try {
//                 const response = await axios.get(`${baseURL}/api/dashboard/get-chart-details`, {
//                     params: params,
//                     headers: {
//                         Authorization: `${token}`,
//                     }
//                 });

//                 if (response && response.data.success) {
//                     let responseData = response?.data?.data;
//                     switch (activeTab) {
//                         case 'revenue':
//                             setTotalDataCount(0);
//                             setData(responseData?.revenue?.data.sort((a, b) => new Date(a._id.year, a._id.month, a._id.day) - new Date(b._id.year, b._id.month, b._id.day)));
//                             setRevenueList(responseData?.revenue?.data.sort((a, b) => new Date(a._id.year, a._id.month, a._id.day) - new Date(b._id.year, b._id.month, b._id.day)))
//                             setTotalDataCount(responseData?.revenue?.totalCount[0]?.count);
//                             setLoading(false);
//                             break;
//                         case 'order':
//                             setTotalDataCount(0);
//                             setData(responseData?.order.data.sort((a, b) => new Date(a._id.year, a._id.month, a._id.day) - new Date(b._id.year, b._id.month, b._id.day)));
//                             setTotalDataCount(responseData?.order.totalCount[0].count);
//                             setLoading(false);
//                             break;
//                         case 'customer':
//                             setTotalDataCount(0);
//                             setData(responseData.customer?.data.sort((a, b) => new Date(a._id.year, a._id.month, a._id.day) - new Date(b._id.year, b._id.month, b._id.day)));
//                             setTotalDataCount(responseData?.customer.totalCount[0].count);
//                             setLoading(false);
//                             break;
//                         default:
//                             setData([]);
//                             setTotalDataCount(0);
//                             setLoading(false);
//                             break;
//                     }
//                 }
//             } catch (error) {
//                 console.error('Error fetching data:', error);
//                 setLoading(false);
//             }
//         };

//         fetchData();
//     }, [activeTab, startDate, categoryId]);

//     useEffect(() => {
//         fetchStores();
//     }, [])

//     const totalCount = data.reduce((acc, curr) => acc + curr.count, 0);

//     function capitalizeFirstLetter(string) {
//         return string?.charAt(0)?.toUpperCase() + string.slice(1);
//     }
//     const handleReportDownload = () => {
//         const unit = "pt";
//         const size = "A4"; // Use A1, A2, A3 or A4
//         const orientation = "portrait"; // portrait or landscape
//         const doc = new jsPDF(orientation, unit, size);

//         // Headers for each report
//         const headers = ['Date', 'No. of Orders', 'Total Revenue'];
//         let pdfData = [];

//         // Extract data from the response for each report type
//         const reports = ['revenue', 'order', 'customer'];
//         reports.forEach(reportType => {
//             // Extract data based on the report type
//             let reportData = [];
//             switch (reportType) {
//                 case 'revenue':
//                     reportData = data || [];
//                     break;
//                 case 'order':
//                     reportData = data || [];
//                     break;
//                 case 'customer':
//                     reportData = data || [];
//                     break;
//                 default:
//                     reportData = [];
//                     break;
//             }

//             // Add a section title for each report
//             pdfData.push([{ content: `${capitalizeFirstLetter(reportType)} Data`, colSpan: 3, styles: { halign: 'center' } }]);

//             // Add report data
//             reportData.forEach(entry => {
//                 pdfData.push([
//                     moment(entry?._id).format('MMMM Do YYYY'), // Assuming _id represents the date
//                     entry?.count || 0,
//                     entry?.totalCount || 0
//                 ]);
//             });

//             // Add an empty row as a separator between reports
//             pdfData.push(['', '', '']);
//         });

//         // Add data to PDF
//         doc.autoTable({
//             head: [headers],
//             body: pdfData
//         });

//         // Save PDF
//         doc.save(`all_reports_${moment().format('YYYY-MM-DD')}.pdf`);
//     };

//     const downloadExcel = () => {
//         // Extracting revenue data
//         const revenueData = [
//             ['Date', 'No. of Orders', 'Total Revenue']
//         ];

//         revenueList.forEach(item => {
//             const date = `${item._id.year}-${item._id.month}-${item._id.day}`;
//             revenueData.push([date, item.count, item.totalCount]);
//         });

//         const workbook = XLSX.utils.book_new();

//         console.log("data?.revenue?.data", data);

//         // Add revenue sheet
//         const revenueWorksheet = XLSX.utils.aoa_to_sheet(revenueData);
//         XLSX.utils.book_append_sheet(workbook, revenueWorksheet, 'Revenue');

//         // Convert workbook to buffer
//         const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

//         // Convert buffer to Blob
//         const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

//         // Create download link and trigger download
//         const excelFilename = `revenue_${moment().format('YYYY-MM-DD')}.xlsx`;
//         const excelDownloadLink = document.createElement('a');
//         excelDownloadLink.href = URL.createObjectURL(excelBlob);
//         excelDownloadLink.download = excelFilename;
//         excelDownloadLink.click();
//     };



//     return (
//         <Fragment>
//             <Col xl="12" md="12">
//                 <Card>

//                     <div className="card-header d-flex align-items-center justify-between" style={{ padding: '15px 30px' }}>
//                         <Nav tabs className='product_variant_tabs'>
//                             <NavItem>
//                                 <NavLink className={activeTab === 'revenue' ? 'active' : ''} onClick={() => handleTabClick('revenue')}>
//                                     Revenue
//                                 </NavLink>
//                             </NavItem>
//                             <NavItem>
//                                 <NavLink className={activeTab === 'order' ? 'active' : ''} onClick={() => handleTabClick('order')}>
//                                     Order
//                                 </NavLink>
//                             </NavItem>
//                             <NavItem>
//                                 <NavLink className={activeTab === 'customer' ? 'active' : ''} onClick={() => handleTabClick('customer')}>
//                                     Customer
//                                 </NavLink>
//                             </NavItem>
//                         </Nav>
//                         <div className='d-flex align-items-center file-content file-content-dash'>
//                             {/* <Input type='select'>
//                                 <option value=''>All Stores</option>
//                                 {storeData && storeData.length > 0 && storeData.map((data) => (
//                                     <option key={data._id} value={data._id}>{data.storeName}</option>
//                                 ))}
//                             </Input> */}
//                             <Input type='select' name='product_id' className='ms-2' onChange={(e) => setCategoryId(e.target.value)}>
//                                 <option value=''>All Category</option>
//                                 {categoryData && categoryData.length > 0 && categoryData.map((data) => (
//                                     <option key={data._id} value={data._id}>{data.collection_name}</option>
//                                 ))}
//                             </Input>
//                             <DatePicker className="datepickerr form-control digits mx-2"
//                                 selected={startDate}
//                                 onChange={(date) => setStartDate(date)}
//                                 dateFormat="MMM yyyy"
//                                 showMonthYearPicker
//                                 showIcon
//                                 placeholderText='Select Month'
//                             />
//                             {/* {
//                                 activeTab === 'revenue' &&
//                                 <button type='button' className='btn btn-secondary' onClick={downloadExcel}>
//                                     Download Excel
//                                 </button>
//                             } */}
//                             {/* <Button type='button' className='filter_btn'>
//                                 <Filter color='#1D2433CC' size={16} />
//                             </Button> */}
//                         </div>
//                     </div>

//                     <CardBody className="chart-block" style={{ padding: '15px' }}>
//                         {data && (
//                             <div style={{ position: 'relative' }}>
//                                 <Bar
//                                     data={{
//                                         labels: Array.from({ length: moment(startDate).daysInMonth() }, (_, i) => moment(startDate).startOf('month').add(i, 'days').format('DD')), //changed to single date
//                                         datasets: [{
//                                             label: 'Count',
//                                             data: Array.from({ length: moment(startDate).daysInMonth() }, (_, i) => {
//                                                 const date = moment(startDate).startOf('month').add(i, 'days').format('YYYY-MM-DD');
//                                                 const dayData = data.find(entry => {
//                                                     const day = entry._id.day.toString().padStart(2, '0');
//                                                     const month = entry._id.month.toString().padStart(2, '0');
//                                                     const year = entry._id.year;
//                                                     return `${year}-${month}-${day}` === date;
//                                                 });
//                                                 return dayData ? (activeTab === 'revenue' ? (dayData.totalCount)?.toFixed(2) : dayData.count?.toFixed(2)) : 0;
//                                             }),
//                                             backgroundColor: "rgb(211, 23, 138)",
//                                             borderRadius: 5,
//                                             borderWidth: 2,
//                                             datalabels: {
//                                                 color: 'black',
//                                                 anchor: 'end',
//                                                 align: 'top'
//                                             }
//                                         }]
//                                     }}
//                                     options={{
//                                         plugins: {
//                                             legend: {
//                                                 display: false,
//                                             },

//                                             title: {
//                                                 display: true,
//                                                 position: 'bottom',
//                                                 text: startDate ? moment(startDate).format('MMM YYYY') : 'Custom Chart Title',
//                                                 padding: {
//                                                     // bottom: 20,
//                                                     top: 20
//                                                 },
//                                             },

//                                         },
//                                         maintainAspectRatio: false,
//                                         responsive: true,
//                                         layout: {
//                                             padding: {
//                                                 top: 50,
//                                                 bottom: 20,
//                                             },
//                                         }
//                                     }}
//                                 />
//                                 <div >
//                                     <h5 style={{ position: 'absolute', top: 0, left: 0, margin: '0' }} className='fw-bolder'>{capitalizeFirstLetter(activeTab)}</h5>
//                                     <p style={{ position: 'absolute', top: 0, right: 0 }}>
//                                         {activeTab !== 'customer' && (
//                                             <span>
//                                                 <b>Total {capitalizeFirstLetter(activeTab)}:</b> {`${activeTab === 'revenue' ? '$' : ''}`}{totalDataCount ? totalDataCount?.toFixed(2) : 0}
//                                             </span>
//                                         )}
//                                     </p>
//                                     {/* <p>Total Order Count: {totalOrderCount}</p> */}
//                                 </div>
//                             </div>
//                         )}
//                     </CardBody>







//                     {/* <CardBody className="chart-block" style={{ padding: '15px' }}>
//                         {data && (
//                             <div style={{ position: 'relative' }}>
//                                 <Bar
//                                     data={{
//                                         labels: data.map(entry => `${entry._id.day}`),
//                                         datasets: [{
//                                             label: 'Count',
//                                             data: data.map(entry => entry.count),
//                                             backgroundColor: "rgb(211, 23, 138)",
//                                             borderRadius: 5,
//                                             borderWidth: 2,
//                                             datalabels: {
//                                                 color: 'black',
//                                                 anchor: 'end',
//                                                 align: 'top'
//                                             }
//                                         }]
//                                     }}
//                                     options={{
//                                         plugins: {
//                                             legend: {
//                                                 display: false,
//                                             },

//                                             title: {
//                                                 display: true,
//                                                 position: 'bottom',
//                                                 text: startDate ? moment(startDate).format('MMM YYYY') : 'Custom Chart Title',
//                                                 padding: {
//                                                     // bottom: 20,
//                                                     top: 20
//                                                 },
//                                             },

//                                         },

//                                         maintainAspectRatio: false,
//                                         responsive: true,
//                                         layout: {
//                                             padding: {
//                                                 top: 50,
//                                                 bottom: 20,
//                                             },
//                                         },
//                                     }}
//                                 />
//                                 <div >
//                                     <h5 style={{ position: 'absolute', top: 0, left: 0, margin: '0' }} className='fw-bolder'>{capitalizeFirstLetter(activeTab)}</h5>
//                                     <p style={{ position: 'absolute', top: 0, right: 0 }}>
//                                         {activeTab !== 'customer' && (
//                                             <span>
//                                                 <b>Total {capitalizeFirstLetter(activeTab)}:</b> ${totalDataCount ? totalDataCount : 0}
//                                             </span>
//                                         )}
//                                     </p>
//                                     {/* <p>Total Order Count: {totalOrderCount}</p> */}
//                     {/* </div>
//                             </div>
//                         )}
//                     </CardBody> */}

//                 </Card>
//             </Col>
//         </Fragment>
//     )
// }

// export default BarChartClass;
